import React from "react";
import { Link } from "react-router-dom";

import { Button, Icon, IconButton, Money, Toggle } from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import { fxCode } from "../../core/currency.js";
import { deriveStockStateFromRow } from "./StockStatePill.jsx";
import "./core-list-card.scss";

/**
 * ONE Core List reference, as a card (Alex, 2026-10-09: "Keep only grid view.
 * Remove list view. Completely reimagine the UI and make it very appealing.")
 *
 * The table is gone. A table gave every reference five equal columns, so the
 * watch — the only thing a dealer actually recognises — was a 56px thumbnail
 * in a 52px row, and the thing the page exists to answer (is anyone selling
 * this at my price?) was a caret in the fourth column.
 *
 * The card inverts that. The photo leads, because that is how the reference is
 * recognised. The answer rides on top of the photo as a ribbon, so a page of
 * 119 references can be read by colour alone before any text. Everything you
 * can DO sits in one footer, in one place, instead of a trailing column.
 *
 * Nothing here is new behaviour: the toggle, Create / Remove (the × on the
 * photo) and the target disclosure are the same handlers the rows used.
 */
const CoreListCard = ({
  row,
  brand,
  model,
  view,
  entry,
  isOpen,
  onToggleTargets,
  onOpenMatches,
  onToggleActive,
  onCreate,
  onRemove,
}) => {
  const { active, busy, canReuse, canCreate, hasListing, removing } = view;

  // Same rule as the Sold pill on Insights, so the two pages never disagree.
  // A sold listing has nothing to switch on or off: the toggle gives way to
  // the state itself, and Create (a clean copy of it) lists it again.
  const isSold = deriveStockStateFromRow(row) === "sold";

  const imageUrl = row.image_file_id ? getFileUrl(row.image_file_id) : null;
  const specs = [row.case_material, row.case_diameter].filter(Boolean).join(" · ");

  const tradeHits = Number(entry?.hit_count) || 0;
  const onlineHits = Number(entry?.marketplace?.match_count) || 0;
  // Live auction lots currently under target (Stock Alert, 2026-11-02). Its
  // own pill: a bid is a floor that can still rise, which is neither a
  // dealer quote nor a listing price.
  const auctionHits = Number(entry?.auction?.match_count) || 0;
  const found = tradeHits + onlineHits + auctionHits;
  const hasTarget = (entry?.targets?.length || 0) > 0;

  // The most recent listing's year and asking price, sold or not (Guillaume,
  // 2026-10-12). Price is minor units; a NULL currency is legacy GBP.
  //
  // One line per piece in stock (Guillaume, 2026-10-13: "three items of the
  // same reference, three lines"). `listings` is the API's unsold set; the
  // single fields are the fallback for a payload that predates it.
  const listingLines = row.listings?.length
    ? row.listings
    : row.listing_price != null || row.listing_year
      ? [{ price: row.listing_price, currency: row.listing_currency, year: row.listing_year }]
      : [];

  // Quotes that arrived and did NOT clear the target. Shown only when nothing
  // did, as a muted ribbon rather than a green one: it is the difference
  // between "this watch is never quoted here" and "it is quoted constantly,
  // just never at your price" — which is the question behind every "it says
  // zero and I don't believe it".
  //
  // It is also the ONLY way into the match view for a reference with no hits,
  // now that the button at the bottom of the panel is gone (Alex, 2026-10-12).
  const missed = Number(entry?.above_count) || 0;

  // The photo and the reference text open the card's panel too (Guillaume,
  // 2026-10-12): the card is what you look at, so it is what you click. Same
  // handler as the target row, which stays the keyboard path, so no second
  // focusable element. Controls keep their own click (same guard as
  // RCListingCard). A click on an open card closes it again — open and close
  // from the same place (Guillaume, 2026-10-12).
  const onCardClick = (e) => {
    if (e.target.closest("button, a, input, label, [role='switch']")) return;
    if (!e.target.closest(".cl-card__media, .cl-card__body")) return;
    onToggleTargets(row.core_list_item_id);
  };

  return (
    <article
      className={`cl-card${found > 0 ? " is-found" : ""}${isOpen ? " is-open" : ""}`}
      onClick={onCardClick}
    >
      <div className="cl-card__media">
        {imageUrl ? (
          <img src={imageUrl} alt="" loading="lazy" />
        ) : (
          <span className="cl-card__media-empty" aria-hidden="true" />
        )}

        {/* The answer the page exists to give, on the photo so a wall of
            references can be scanned by colour before any text is read.
            Trade and online stay separate: a trade quote was checked against
            the target in the store's own currency, an online listing is the
            scraper's assertion. */}
        {/* A BUTTON since 2026-10-12, not a label: the count is the fastest
            way into the matches, and a number that answers the page's whole
            question should be clickable. Falls back to a plain span when no
            handler is passed, so nothing breaks where the card is reused. */}
        {found > 0 &&
          (onOpenMatches ? (
            <button
              type="button"
              className="cl-card__found is-clickable"
              onClick={() => onOpenMatches(row.core_list_item_id)}
              aria-label={`See the ${tradeHits} trade quotes at or under target for ${row.reference || "this reference"}`}
            >
              {tradeHits > 0 && <span className="cl-card__found-trade">{tradeHits} at target</span>}
              {onlineHits > 0 && <span className="cl-card__found-online">{onlineHits} online</span>}
              {auctionHits > 0 && <span className="cl-card__found-auction">{auctionHits} at auction</span>}
            </button>
          ) : (
            <span className="cl-card__found">
              {tradeHits > 0 && <span className="cl-card__found-trade">{tradeHits} at target</span>}
              {onlineHits > 0 && <span className="cl-card__found-online">{onlineHits} online</span>}
              {auctionHits > 0 && <span className="cl-card__found-auction">{auctionHits} at auction</span>}
            </span>
          ))}

        {found === 0 && missed > 0 && onOpenMatches && (
          <button
            type="button"
            className="cl-card__found is-clickable is-missed"
            onClick={() => onOpenMatches(row.core_list_item_id)}
            aria-label={`See the ${missed} quotes that did not meet the target for ${row.reference || "this reference"}`}
          >
            <span className="cl-card__found-missed">{missed} quoted</span>
          </button>
        )}

        {/* Remove lives on the photo's corner, where the listing-count badge
            was (Guillaume, 2026-10-12: the count and its S / M split meant
            nothing to him). Same handler as the footer button it replaces,
            so the confirmation and the rollback are unchanged. */}
        <IconButton
          size="sm"
          round
          label="Remove from list"
          className="cl-card__remove"
          disabled={removing}
          onClick={() => onRemove(row)}
        >
          <Icon name="X" size="sm" />
        </IconButton>
      </div>

      <div className="cl-card__body">
        {row.product_id ? (
          <Link to={`/view-product/${row.product_id}`} className="cl-card__ref">
            {row.reference || "—"}
          </Link>
        ) : (
          <span className="cl-card__ref">{row.reference || "—"}</span>
        )}
        <span className="cl-card__name">
          {brand}
          {model ? ` · ${model}` : ""}
        </span>
        {specs && <span className="cl-card__specs">{specs}</span>}
      </div>

      {/* The target is the card's primary action, so it is a full-width button
          rather than a value with a caret hidden among four other columns. */}
      <button
        type="button"
        className={`cl-card__target${hasTarget ? " has-target" : ""}`}
        onClick={() => onToggleTargets(row.core_list_item_id)}
        aria-expanded={isOpen}
        aria-label={`${hasTarget ? "Edit" : "Set"} buy target for ${row.reference || "this reference"}`}
      >
        {/* The row is still the way into the target editor; it leads with the
            listing, price first (Guillaume, 2026-10-13). The target itself
            lives only inside the panel, not on the card. */}
        <span className="cl-card__listing">
          {listingLines.map((line, i) => (
            <span key={line.id ?? i} className="cl-card__listing-line">
              {line.price != null && (
                <Money
                  value={line.price}
                  currency={fxCode(line.currency) || "gbp"}
                  decimals={0}
                />
              )}
              {line.year && line.price != null && " · "}
              {line.year}
            </span>
          ))}
        </span>
        <span className="cl-card__target-caret" aria-hidden="true">
          {isOpen ? "▲" : "▾"}
        </span>
      </button>

      <div className="cl-card__foot">
        {isSold ? (
          <span className="cl-card__foot-label">Listing sold</span>
        ) : hasListing ? (
          <Toggle
            size="md"
            tone="active"
            checked={active}
            onChange={(val) => onToggleActive(row, val)}
            disabled={busy || row.most_recent_listing_is_sold === true}
            // No visible word: a green switch already says a listing is live
            // (Guillaume, 2026-10-13). The name stays for screen readers.
            aria-label="Active"
          />
        ) : (
          <span className="cl-card__foot-label">No listing</span>
        )}

        <span className="cl-card__actions">
          {(canCreate || canReuse) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onCreate(row)}
            >
              Create
            </Button>
          )}
        </span>
      </div>
    </article>
  );
};

export default CoreListCard;
